import { z } from 'zod';

export const ChatRoleSchema = z.enum(['user', 'assistant']);
export type ChatRole = z.infer<typeof ChatRoleSchema>;

export const ChatMessageSchema = z.object({
  role: ChatRoleSchema,
  content: z.string().min(1),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

/**
 * Friendly model ids. The unversioned Anthropic names (`sonnet`, `opus`, …)
 * track the current release of that family; a versioned entry beside one
 * (`sonnet-4-6`) is a superseded release kept so its historical spend still
 * prices — cost rows record the id that ran, and a model missing from the
 * registry stays unpriced rather than borrowing another model's rate.
 */
export const ChatModelSchema = z.enum([
  'sonnet',
  'sonnet-4-6',
  'opus',
  'opus-5',
  'haiku',
  'fable',
  'fable-5',
  'gpt-6-astra',
  'gpt-6.1-sol',
  'gpt-5.6-sol',
  'gpt-5.6-terra',
  'gpt-5.6-luna',
  'gpt-5.5',
  'gpt-5.4-mini',
  'gpt-5.5-pro',
  'gemini-2.5-pro',
  'gemini-2.5-flash',
  'grok-4.20',
  'grok-4.1-fast',
  'glm-5.2',
  'glm-5.2-runloop',
]);
export type ChatModel = z.infer<typeof ChatModelSchema>;

export const ChatProviderSchema = z.enum([
  'anthropic',
  'openai',
  'google',
  'xai',
  'nebius',
  'runloop',
]);
export type ChatProvider = z.infer<typeof ChatProviderSchema>;

export const ChatModelStatusSchema = z.object({
  id: ChatModelSchema,
  provider: ChatProviderSchema,
  enabled: z.boolean(),
  hasKey: z.boolean(),
});
export type ChatModelStatus = z.infer<typeof ChatModelStatusSchema>;

export const ChatRequestSchema = z.object({
  messages: z.array(ChatMessageSchema).min(1),
  model: ChatModelSchema,
  system: z.string().optional(),
});
export type ChatRequest = z.infer<typeof ChatRequestSchema>;

export const ConversationSchema = z.object({
  id: z.string(),
  title: z.string(),
  model: ChatModelSchema,
  mcpServerIds: z.array(z.string()).default([]),
  providerSecretId: z.string().nullable().default(null),
  /** Epoch-ms when the conversation was archived; null means active. */
  archivedAt: z.number().nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Conversation = z.infer<typeof ConversationSchema>;

export const ConversationWithMessagesSchema = ConversationSchema.extend({
  messages: z.string(),
});
export type ConversationWithMessages = z.infer<typeof ConversationWithMessagesSchema>;

export const CreateConversationRequestSchema = z.object({
  title: z.string().optional(),
  model: ChatModelSchema.optional(),
  mcpServerIds: z.array(z.string()).optional(),
  providerSecretId: z.string().nullable().optional(),
});
export type CreateConversationRequest = z.infer<typeof CreateConversationRequestSchema>;

export const UpdateConversationRequestSchema = z.object({
  title: z.string().optional(),
  model: ChatModelSchema.optional(),
  messages: z.string().optional(),
  mcpServerIds: z.array(z.string()).optional(),
  providerSecretId: z.string().nullable().optional(),
});
export type UpdateConversationRequest = z.infer<typeof UpdateConversationRequestSchema>;
